import { NormalizedRecordUtil } from "@base/util/NormalizedRecord";
import { GetTaggedType } from "@base/util/TypeUtil";
import { DealerType } from "@biz/definitions/AccessDefinitions";
import { FeatureLevel } from "@biz/definitions/ProductDefinitions";
import { ICurtain2dForm } from "@biz/items/curtain/CurtainDefinitions";
import { IProduct2dForm, ISail2dForm } from "@biz/models/Sail2dDefinitions";
import { Sail2dForm } from "@biz/models/Sail2dForm";
import { ShoppingList } from "@biz/models/ShoppingList";
import { IVariantData2d, Product2dRecord, product2dTypeInfos } from "@biz/userProjects/new/IVariant";

const productTypes2d: IProduct2dForm["productType"][] = ["sunSail2d", "curtain2d", "standardCollection2d"];
const productRecords = productTypes2d.map((p) => product2dTypeInfos[p]?.data).filter((p): p is Product2dRecord => !!p)

export function createDefaultSunSail2d(featureLevel: FeatureLevel, dealerType: DealerType | undefined): ISail2dForm {
  if (dealerType === DealerType.RS)
    return Sail2dForm.createSolidayC4("2022", featureLevel);
  else
    return Sail2dForm.createSolidayFixed4("2022", featureLevel);
}

export class VariantData2d {
  static create(): IVariantData2d {
    return {
      type: "sail2d",
      tabId: "",
      sunSails: NormalizedRecordUtil.createEmpty(),
      raffs: NormalizedRecordUtil.createEmpty(),
      curtains: NormalizedRecordUtil.createEmpty(),
      lounges: NormalizedRecordUtil.createEmpty(),
      standardCollections: NormalizedRecordUtil.createEmpty(),
      shoppingList: ShoppingList.createEmpty(),
    }
  }

  static createDefault(featureLevel: FeatureLevel, dealerType: DealerType | undefined): IVariantData2d {
    const sunSail = createDefaultSunSail2d(featureLevel, dealerType);
    const empty = VariantData2d.create();
    return {
      ...empty,
      tabId: sunSail.id,
      sunSails: NormalizedRecordUtil.createOne(sunSail.id, sunSail)
    }
  }

  static getProductRecords(): Product2dRecord[] {
    return productRecords;
  }

  static findRecordForItemId(data: IVariantData2d, id: string): Product2dRecord | undefined {
    for (const p of VariantData2d.getProductRecords()) {
      const record = data[p];
      if (record && record.byId[id]) {
        return p;
      }
    }
    return undefined;
  }

  static getProduct(data: IVariantData2d | undefined, id: string): IProduct2dForm | undefined {
    if (!data)
      return undefined;
    for (const p of VariantData2d.getProductRecords()) {
      const record = data[p];
      if (record && record.byId[id]) {
        return record.byId[id];
      }
    }
    return undefined;
  }

  static getProducts(data: IVariantData2d): IProduct2dForm[] {
    if (data.type !== "sail2d") {
      return [];
    }
    const ret: IProduct2dForm[] = [];
    const productRecords = VariantData2d.getProductRecords();
    for (const p of productRecords) {
      const record = data[p];
      if (record?.byId) {
        for (const k of Object.keys(record.byId)) {
          ret.push(record.byId[k] as IProduct2dForm)
        }
      }
    }
    return ret;
  }


  static getProductRecord<T extends Product2dRecord>(data: IVariantData2d, recordType: T): IVariantData2d[T]["byId"] {
    return data?.[recordType]?.byId
  }

  static getSunSail(data: IVariantData2d, id: string): ISail2dForm | undefined {
    return VariantData2d.getProductRecord(data, "sunSails")?.[id];
  }

  static getTyped<T extends IProduct2dForm["productType"]>(type: T, data: IVariantData2d, id: string): GetTaggedType<IProduct2dForm, { productType: T }> | undefined {
    const t = VariantData2d.getProductRecord(data, product2dTypeInfos[type].data)[id];
    if (t) {
      return t as GetTaggedType<IProduct2dForm, { productType: T }>;
    }
    return undefined;
  }

  static getCurtain(data: IVariantData2d, id: string): ICurtain2dForm | undefined {
    return VariantData2d.getProductRecord(data, "curtains")?.[id];
  }

  static getProductIds(data: IVariantData2d): string[] {
    const ids: string[] = [];
    for (const p of VariantData2d.getProductRecords()) {
      const newIds = data[p]?.ids;
      if (newIds)
        ids.push(...newIds)
    }
    return ids;
  }
}